import React, { useState } from "react";
import { useLang } from "@/context/LanguageContext";
import { api } from "@/lib/api";
import { toast } from "sonner";

export default function CouponField({ coupon, onChange }) {
  const { t } = useLang();
  const [open, setOpen] = useState(false);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);

  const apply = async () => {
    const trimmed = code.trim().toUpperCase();
    if (!trimmed) return;
    setBusy(true);
    try {
      const { data } = await api.post("/coupons/validate", { code: trimmed });
      onChange(data);
      toast.success(t("coupon.applied").replace("{code}", data.coupon.code));
    } catch (err) {
      onChange(null);
      toast.error(err?.response?.data?.detail || t("coupon.invalid"));
    } finally { setBusy(false); }
  };

  const remove = () => { onChange(null); setCode(""); setOpen(false); };

  if (!open && !coupon) {
    return (
      <div data-testid="coupon-section">
        <button type="button" onClick={() => setOpen(true)} className="text-xs uppercase tracking-[0.2em] text-gold hover:text-gold-hi inline-flex items-center gap-2" data-testid="coupon-toggle-btn">
          <span className="w-5 h-5 rounded-full border border-gold-active flex items-center justify-center text-[10px]">%</span>
          {t("coupon.have")}
        </button>
      </div>
    );
  }

  return (
    <label className="block" data-testid="coupon-section">
      <span className="block text-xs uppercase tracking-[0.2em] text-gold mb-2">{t("coupon.label")}</span>
      {coupon ? (
        <div className="flex items-center justify-between gap-3 rounded-xl border border-gold-active bg-gold/10 px-4 py-3" data-testid="coupon-applied">
          <div>
            <div className="text-gold-hi text-sm font-medium tracking-widest">{coupon.coupon.code}</div>
            <div className="text-[11px] text-muted-warm">
              <span className="line-through mr-2">₹{coupon.list_paise / 100}</span>
              <span className="text-ivory">₹{coupon.final_paise / 100}</span>
              <span className="ml-2">(−₹{coupon.discount_paise / 100})</span>
            </div>
          </div>
          <button type="button" onClick={remove} className="text-[11px] text-muted-warm hover:text-red-300 underline" data-testid="coupon-remove-btn">{t("coupon.remove")}</button>
        </div>
      ) : (
        <div className="flex gap-2">
          <input
            data-testid="coupon-input"
            type="text"
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); apply(); } }}
            placeholder={t("coupon.ph")}
            className="input-warm flex-1 uppercase tracking-widest"
            autoComplete="off"
          />
          <button type="button" onClick={apply} disabled={busy || !code} className="btn-outline-gold text-xs px-5 disabled:opacity-50" data-testid="coupon-apply-btn">
            {busy ? "…" : t("coupon.apply")}
          </button>
        </div>
      )}
    </label>
  );
}
